import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, initials, romeDayKey, fmtTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, LogIn, LogOut, Coffee, Clock, CalendarOff, CalendarPlus,
  Plus, TrendingUp, Users, Percent, Timer, Wallet, Settings2, Ban,
} from "lucide-react";

// Stato presenza -> etichetta + token colore
const STATUS_META = {
  in: { label: "In servizio", token: "--hi-status-success" },
  break: { label: "In pausa", token: "--hi-gold" },
  out: { label: "Non timbrato", token: "--muted-foreground" },
  done: { label: "Uscito", token: "--muted-foreground" },
  ferie: { label: "In ferie", token: "--hi-status-info" },
  assente: { label: "Assente", token: "--destructive" },
};

const ABSENCE_TYPES = [
  { v: "ferie", l: "Ferie" },
  { v: "permesso", l: "Permesso" },
  { v: "malattia", l: "Malattia" },
  { v: "assenza", l: "Assenza / blocco fascia" },
  { v: "formazione", l: "Formazione" },
  { v: "giorno_libero", l: "Giorno libero" },
];

const fmtHM = (min) => {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

function InfoTile({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className={`font-heading text-lg font-semibold ${tone || ""}`}>{value}</div>
    </div>
  );
}

export function OperatorDrawer({ open, onOpenChange, operatorId, date, onNewAppointment, onSelectAppointment, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const dateStr = romeDayKey(date || new Date());

  const load = useCallback(async () => {
    if (!operatorId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/operators/${operatorId}/agenda-panel?date=${dateStr}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento pannello");
      setData(null);
    } finally { setLoading(false); }
  }, [operatorId, dateStr]);

  useEffect(() => { if (open && operatorId) load(); }, [open, operatorId, load]);

  const canClock = data && (data.is_self || data.can_manage_attendance);
  const status = data?.attendance?.status || "out";
  const meta = STATUS_META[status] || STATUS_META.out;

  const clockAction = async (path, msg) => {
    if (!canClock) return;
    setBusy(true);
    try {
      await api.post(`/attendance/${path}`, data.is_self ? {} : { operator_id: operatorId });
      toast.success(msg);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore timbratura"); }
    finally { setBusy(false); }
  };

  // ---- Dialog: Ferie / Permesso / Blocco fascia (assenza) ----
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leave, setLeave] = useState({ type: "ferie", full_day: true, start_t: "09:00", end_t: "13:00", note: "" });
  const submitLeave = async () => {
    setBusy(true);
    try {
      const start = leave.full_day
        ? new Date(`${dateStr}T00:00:00`)
        : new Date(`${dateStr}T${leave.start_t}:00`);
      const end = leave.full_day
        ? new Date(`${dateStr}T23:59:59`)
        : new Date(`${dateStr}T${leave.end_t}:00`);
      if (end <= start) { toast.error("Intervallo non valido"); setBusy(false); return; }
      const res = await api.post("/team/absences", {
        operator_id: operatorId, type: leave.type, full_day: leave.full_day,
        start: start.toISOString(), end: end.toISOString(), note_staff: leave.note,
      });
      const st = res.data?.absence?.status;
      toast.success(st === "pending" ? "Richiesta inviata (in attesa)" : "Assenza registrata");
      const conflicts = res.data?.conflicts || [];
      if (conflicts.length) toast.warning(`${conflicts.length} appuntamento/i in conflitto in questa fascia`);
      setLeaveOpen(false); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // ---- Dialog: Turno del giorno / Disponibilità straordinaria ----
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shift, setShift] = useState({ start_hm: "09:00", end_hm: "19:00", break_start_hm: "", break_end_hm: "", note: "" });
  const submitShift = async () => {
    setBusy(true);
    try {
      await api.post("/team/shift-exceptions", {
        operator_id: operatorId, date: dateStr, type: "custom",
        start_hm: shift.start_hm, end_hm: shift.end_hm,
        break_start_hm: shift.break_start_hm || null, break_end_hm: shift.break_end_hm || null,
        note: shift.note,
      });
      toast.success("Turno del giorno aggiornato");
      setShiftOpen(false); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const qi = data?.quick_info || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto hi-scroll" data-testid="operator-drawer">
        {loading || !data ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" />
          </div>
        ) : (
          <>
            <SheetHeader className="mb-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11">
                  <AvatarFallback style={{ background: (data.operator.color || "#C9A24B") + "22", color: data.operator.color }}>
                    {initials(data.operator.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <SheetTitle className="font-heading text-xl leading-tight truncate" data-testid="drawer-operator-name">
                    {data.operator.name}
                  </SheetTitle>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground capitalize">{data.operator.role}</span>
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
                      data-testid="drawer-status-badge"
                      style={{ color: `hsl(var(${meta.token}))`, backgroundColor: `hsl(var(${meta.token}) / 0.12)`, border: `1px solid hsl(var(${meta.token}) / 0.3)` }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: `hsl(var(${meta.token}))` }} />
                      {meta.label}
                    </span>
                  </div>
                </div>
              </div>
            </SheetHeader>

            {/* Timbratura */}
            <div className="rounded-lg border border-border p-3 mb-4" data-testid="drawer-timbratura">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Presenza</span>
                {data.attendance.clock_in && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {fmtTime(data.attendance.clock_in)}{data.attendance.clock_out ? ` – ${fmtTime(data.attendance.clock_out)}` : ""}
                  </span>
                )}
              </div>
              {canClock ? (
                <div className="flex flex-wrap gap-2">
                  {status === "out" && <Button size="sm" onClick={() => clockAction("clock-in", "Entrata registrata")} disabled={busy} data-testid="drawer-clock-in"><LogIn className="h-4 w-4 mr-1.5" /> Timbra entrata</Button>}
                  {status === "in" && <>
                    <Button size="sm" variant="outline" onClick={() => clockAction("break-start", "Pausa iniziata")} disabled={busy} data-testid="drawer-break-start"><Coffee className="h-4 w-4 mr-1.5" /> Pausa</Button>
                    <Button size="sm" onClick={() => clockAction("clock-out", "Uscita registrata")} disabled={busy} data-testid="drawer-clock-out"><LogOut className="h-4 w-4 mr-1.5" /> Timbra uscita</Button>
                  </>}
                  {status === "break" && <>
                    <Button size="sm" variant="outline" onClick={() => clockAction("break-end", "Pausa terminata")} disabled={busy} data-testid="drawer-break-end"><Coffee className="h-4 w-4 mr-1.5" /> Fine pausa</Button>
                    <Button size="sm" onClick={() => clockAction("clock-out", "Uscita registrata")} disabled={busy} data-testid="drawer-clock-out"><LogOut className="h-4 w-4 mr-1.5" /> Timbra uscita</Button>
                  </>}
                  {status === "done" && <span className="text-sm text-[hsl(var(--hi-status-success))] font-medium">Timbratura completata per oggi</span>}
                  {(status === "ferie" || status === "assente") && <span className="text-sm text-muted-foreground">Operatore {meta.label.toLowerCase()} oggi</span>}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Solo l'operatore stesso o un responsabile possono timbrare.</p>
              )}
            </div>

            {/* Quick Info */}
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Info rapide · {dateStr === romeDayKey(new Date()) ? "oggi" : dateStr}</div>
              <div className="grid grid-cols-2 gap-2" data-testid="drawer-quick-info">
                <InfoTile icon={Clock} label="Ore oggi" value={fmtHM(qi.worked_today)} testid="qi-worked-today" />
                <InfoTile icon={TrendingUp} label="Straordinari" value={fmtHM(qi.overtime_today)} tone={qi.overtime_today ? "text-[hsl(var(--hi-gold))]" : ""} testid="qi-overtime" />
                <InfoTile icon={Clock} label="Ore settimana" value={fmtHM(qi.worked_week)} testid="qi-worked-week" />
                <InfoTile icon={Clock} label="Ore mese" value={fmtHM(qi.worked_month)} testid="qi-worked-month" />
                <InfoTile icon={Timer} label="Ritardo" value={fmtHM(qi.delay_today)} tone={qi.delay_today ? "text-destructive" : ""} testid="qi-delay" />
                <InfoTile icon={Coffee} label="Pause" value={fmtHM(qi.break_today)} testid="qi-breaks" />
                <InfoTile icon={Users} label="Appuntamenti" value={qi.appointments_today ?? 0} testid="qi-appts" />
                <InfoTile icon={Percent} label="Occupazione" value={qi.occupancy_pct != null ? `${qi.occupancy_pct}%` : "—"} testid="qi-occupancy" />
                <InfoTile icon={Clock} label="Tempo libero" value={qi.free_min != null ? fmtHM(qi.free_min) : "—"} testid="qi-free" />
                {qi.revenue_today != null && (
                  <InfoTile icon={Wallet} label="Incasso oggi" value={eur(qi.revenue_today)} tone="text-[hsl(var(--hi-gold))]" testid="qi-revenue" />
                )}
              </div>
            </div>

            {/* Appuntamenti del giorno */}
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Appuntamenti del giorno</div>
              <div className="space-y-1.5 max-h-52 overflow-y-auto hi-scroll" data-testid="drawer-appts">
                {data.appointments.length === 0 && <div className="text-sm text-muted-foreground py-3 text-center">Nessun appuntamento.</div>}
                {data.appointments.map((a) => (
                  <button key={a.id} onClick={() => { onSelectAppointment && onSelectAppointment(a.id); }}
                    className="w-full flex items-center gap-3 rounded-lg border border-border p-2 text-left hover:bg-accent transition-colors"
                    data-testid={`drawer-appt-${a.id}`}>
                    <span className="text-sm font-medium tabular-nums w-11">{a.time}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{a.client_name}</span>
                      <span className="block text-xs text-muted-foreground truncate">{(a.services || []).join(", ")}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <Separator className="my-4" />

            {/* Azioni rapide */}
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Azioni rapide</div>
            <div className="grid grid-cols-2 gap-2" data-testid="drawer-quick-actions">
              <Button variant="outline" className="justify-start" onClick={() => { onNewAppointment && onNewAppointment(operatorId); onOpenChange(false); }} data-testid="drawer-new-appt">
                <Plus className="h-4 w-4 mr-1.5" /> Nuovo appuntamento
              </Button>
              <Button variant="outline" className="justify-start" onClick={() => setLeaveOpen(true)} data-testid="drawer-leave-btn">
                <CalendarOff className="h-4 w-4 mr-1.5" /> Ferie / Assenza
              </Button>
              {data.can_manage_shifts && (
                <Button variant="outline" className="justify-start" onClick={() => setShiftOpen(true)} data-testid="drawer-shift-btn">
                  <CalendarPlus className="h-4 w-4 mr-1.5" /> Turno del giorno
                </Button>
              )}
              <Button variant="outline" className="justify-start" onClick={() => { onOpenChange(false); navigate("/team"); }} data-testid="drawer-manage-team">
                <Settings2 className="h-4 w-4 mr-1.5" /> Gestisci turni
              </Button>
            </div>

            {data.shift_windows.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-3">
                Turno di oggi: {data.shift_windows.map((w) => `${w.start}–${w.end}`).join(", ")}
              </p>
            )}
            {data.absence_today && (
              <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: `hsl(var(--hi-status-info))` }}>
                <Ban className="h-3 w-3" /> {data.absence_today.type} · {data.absence_today.status === "pending" ? "in attesa" : "approvata"}
              </p>
            )}
          </>
        )}
      </SheetContent>

      {/* Dialog Ferie / Assenza */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-sm" data-testid="leave-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Ferie / Assenza · {dateStr}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Tipologia</Label>
              <Select value={leave.type} onValueChange={(v) => setLeave((s) => ({ ...s, type: v }))}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ABSENCE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={leave.full_day} onChange={(e) => setLeave((s) => ({ ...s, full_day: e.target.checked }))} data-testid="leave-fullday" />
              Giornata intera
            </label>
            {!leave.full_day && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Dalle</Label><Input type="time" value={leave.start_t} onChange={(e) => setLeave((s) => ({ ...s, start_t: e.target.value }))} data-testid="leave-start" /></div>
                <div className="space-y-1.5"><Label>Alle</Label><Input type="time" value={leave.end_t} onChange={(e) => setLeave((s) => ({ ...s, end_t: e.target.value }))} data-testid="leave-end" /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label>Nota</Label><Textarea rows={2} value={leave.note} onChange={(e) => setLeave((s) => ({ ...s, note: e.target.value }))} data-testid="leave-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>Annulla</Button>
            <Button onClick={submitLeave} disabled={busy} data-testid="leave-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Turno del giorno */}
      <Dialog open={shiftOpen} onOpenChange={setShiftOpen}>
        <DialogContent className="max-w-sm" data-testid="shift-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Turno del giorno · {dateStr}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Inizio</Label><Input type="time" value={shift.start_hm} onChange={(e) => setShift((s) => ({ ...s, start_hm: e.target.value }))} data-testid="shift-start" /></div>
              <div className="space-y-1.5"><Label>Fine</Label><Input type="time" value={shift.end_hm} onChange={(e) => setShift((s) => ({ ...s, end_hm: e.target.value }))} data-testid="shift-end" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Pausa da</Label><Input type="time" value={shift.break_start_hm} onChange={(e) => setShift((s) => ({ ...s, break_start_hm: e.target.value }))} data-testid="shift-break-start" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Pausa a</Label><Input type="time" value={shift.break_end_hm} onChange={(e) => setShift((s) => ({ ...s, break_end_hm: e.target.value }))} data-testid="shift-break-end" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground">Sovrascrive il turno solo per questo giorno (eccezione).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShiftOpen(false)}>Annulla</Button>
            <Button onClick={submitShift} disabled={busy} data-testid="shift-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva turno"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
